// v0.8.9: self-service profile edit page. Mirrors the admin's
// edit-user form on /admin/users but limited to fields a player
// can legitimately change about themselves. Role, is_ignored,
// password, and email are deliberately not editable here \u2014 those
// remain admin-only.
//
// The form is intentionally small and uses the same input/label/
// card chrome as the rest of the app so it feels native.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CurrentUser } from '../../lib/types';

interface MeResponse { user: CurrentUser }

interface EditForm {
  display_name: string;
  handle: string;
  gender: 'male' | 'female' | 'unknown';
  // The admin edit form enumerates known opt_outs. We mirror
  // the same set here so the operator's mental model matches
  // what the player sees.
  opt_in_public_board: boolean;
  // v0.8.8: personal public-board chip colour. NULL = default.
  chip_color: string | null;
}

const PRONOUN_OPTIONS: Array<{ value: EditForm['gender']; label: string; hint: string }> = [
  { value: 'unknown', label: 'Unknown / prefer not to say', hint: 'Chronicles use they/them/their (default).' },
  { value: 'female',  label: 'She / her',                  hint: 'Chronicles use she/her/her.' },
  { value: 'male',    label: 'He / him',                   hint: 'Chronicles use he/him/his.' },
];

// v0.8.8: public-board chip colour palette. Mid-dark shades so
// white chip text stays readable on the dark wall TV.
const CHIP_COLORS = [
  '#2563eb', '#16a34a', '#ea580c', '#9333ea', '#dc2626',
  '#0891b2', '#ca8a04', '#db2777', '#65a30d', '#7c3aed',
];

export function SettingsPage() {
  const authUser = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState<EditForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Narrow authUser through a local const so TS keeps the non-null
  // type across closure boundaries.
  const user = authUser;

  // Hydrate the form once we have the current user. We seed from
  // the auth context so the page renders synchronously when
  // possible (no loading flash) and fall back to a fresh fetch if
  // The auth context's CurrentUser (loaded by AppShell from
  // /api/auth/me) carries gender + opt_in_public_board, so we
  // can initialise the form synchronously from the cached user.
  // v0.8.9 added gender + opt_in_public_board to SessionUser so
  // /api/auth/me carries them too — no extra /api/me fetch.
  useEffect(() => {
    if (!user) return;
    setForm({
      display_name: user.display_name,
      handle: user.handle,
      gender: user.gender ?? 'unknown',
      opt_in_public_board: user.opt_in_public_board ?? true,
      chip_color: (user as CurrentUser & { chip_color?: string | null }).chip_color ?? null,
    });
  }, [user]);

  const save = useMutation({
    mutationFn: (body: Partial<EditForm> & { opt_outs?: Record<string, boolean> }) =>
      api<MeResponse>('/api/me', { method: 'PATCH', body }),
    onSuccess: (res) => {
      setError(null);
      // Update the cached /api/me query so the AppShell header
      // reflects the new display_name immediately.
      void qc.invalidateQueries({ queryKey: ['me'] });
      void qc.setQueryData(['me'], res);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    },
  });

  if (!form) {
    return <div className="text-ink-muted">Loading…</div>;
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    save.mutate({
      display_name: form.display_name.trim(),
      handle: form.handle.trim(),
      gender: form.gender,
      opt_outs: form.opt_in_public_board ? {} : { public_board: true },
      chip_color: form.chip_color,
    });
  };

  const dirty = (
    form.display_name !== user?.display_name ||
    form.handle !== user?.handle
    // gender and opt_in_public_board aren't in the auth context,
    // so we can't compare them cheaply. Send them on every save
    // \u2014 the server treats PATCH as idempotent.
  );

  return (
    <div className="space-y-6">
      <header className="mx-auto max-w-xl">
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-ink-muted">
          Manage how you appear on the public board and in the Chronicles.
          For role or ignore-status changes, contact an admin.
        </p>
      </header>

      {/* v0.8.9: mx-auto centers the card in the available width.
          max-w-xl keeps the form narrow for readability. */}
      <form onSubmit={onSubmit} className="card space-y-4 max-w-xl mx-auto">
        <div className="space-y-1">
          <label className="label" htmlFor="settings-display-name">Display name</label>
          <input
            id="settings-display-name"
            className="input"
            required
            maxLength={120}
            value={form.display_name}
            onChange={(e) => setForm((f) => f ? { ...f, display_name: e.target.value } : f)}
          />
          <p className="text-xs text-ink-dim">Shown on the public board, the Chronicles, and kudos.</p>
        </div>

        <div className="space-y-1">
          <label className="label" htmlFor="settings-handle">Handle</label>
          <input
            id="settings-handle"
            className="input font-mono"
            required
            minLength={2}
            maxLength={40}
            pattern="[a-z0-9_-]+"
            value={form.handle}
            onChange={(e) => setForm((f) => f ? { ...f, handle: e.target.value.toLowerCase() } : f)}
          />
          <p className="text-xs text-ink-dim">
            Lowercase on save. Used as your @-mention on kudos and in the public board's
            live feed. 2–40 characters: a–z, 0–9, underscore, dash.
          </p>
        </div>

        <div className="space-y-1">
          <label className="label" htmlFor="settings-gender">Pronouns (Chronicles)</label>
          <select
            id="settings-gender"
            className="input"
            value={form.gender}
            onChange={(e) => setForm((f) => f ? { ...f, gender: e.target.value as EditForm['gender'] } : f)}
          >
            {PRONOUN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <p className="text-xs text-ink-dim">
            Drives the per-actor pronoun rule in the Chronicles generator. Defaults to
            they/them/their; set this if you'd rather the prose use a different voice.
          </p>
        </div>

        <div className="space-y-1">
          <label className="label flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.opt_in_public_board}
              onChange={(e) => setForm((f) => f ? { ...f, opt_in_public_board: e.target.checked } : f)}
              className="h-4 w-4 rounded border-white/20 bg-bg-panel"
            />
            Show my display name on the public board
          </label>
          <p className="text-xs text-ink-dim">
            When off, the public board shows your contributions anonymously. Your activity
            is still recorded and you still earn XP \u2014 only the wall TV is anonymised.
          </p>
        </div>

        <div className="space-y-2">
          <label className="label">Chip colour (public board)</label>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setForm((f) => f ? { ...f, chip_color: null } : f)}
              className={`rounded-full border px-3 py-1.5 text-xs ${form.chip_color === null ? 'border-accent' : 'border-white/15'}`}
            >
              Default
            </button>
            {CHIP_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setForm((f) => f ? { ...f, chip_color: c } : f)}
                className={`h-7 w-7 rounded-full border-2 ${form.chip_color === c ? 'border-white' : 'border-transparent'}`}
                style={{ backgroundColor: c }}
                aria-label={`chip colour ${c}`}
              />
            ))}
          </div>
          <p className="text-xs text-ink-dim">
            Your name chip on the public board's goal and boss-fight boxes uses this colour.
            Leave on Default for the neutral chip style.
          </p>
        </div>

        <div className="border-t border-white/5 pt-4 flex items-center justify-between">
          <div className="text-sm">
            {error ? <span className="text-accent-danger">{error}</span> : null}
            {save.isSuccess && !error ? <span className="text-accent-success">Saved.</span> : null}
          </div>
          <button
            type="submit"
            className="btn-primary"
            disabled={save.isPending || !form.display_name.trim() || form.handle.trim().length < 2}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>

        <p className="text-xs text-ink-dim">
          Role, ignore status, password, and email can only be changed by an admin
          (Audit &amp; Security policy — privilege escalation guard).
        </p>
      </form>

      {/* v0.8.9: moved from the player dashboard so the headline
          stats get the top of the page. Shares the same
          ['me-progress'] cache the dashboard reads, so picking a
          class on this page immediately updates the level-up
          celebration overlay + any future class display. */}
      <ClassSection />
    </div>
  );
}

// Sub-component: class picker. Reads class_auto / class_choice
// from the same ['me-progress'] query the player dashboard uses,
// and posts the user's choice to /api/me/class. Invalidating
// ['me-progress'] on success means the dashboard + any
// downstream display (level-up celebration, future class
// widget) picks up the change without a page reload.
const CLASS_OPTIONS = [
  { value: 'none',     label: 'Auto',                   icon: '·' },
  { value: 'crafter',  label: 'Crafter',                icon: '🔨' },
  { value: 'guardian', label: 'Guardian',               icon: '🛡️' },
  { value: 'explorer', label: 'Explorer',               icon: '🧭' },
  { value: 'diplomat', label: 'Diplomat',               icon: '🤝' },
];

interface ProgressResponse {
  class_auto?: string | null;
  class_choice?: string | null;
}

function ClassSection() {
  const qc = useQueryClient();
  const progress = useQuery({
    queryKey: ['me-progress'],
    queryFn: () => api<ProgressResponse>('/api/me/progress'),
  });
  const classAuto = progress.data?.class_auto ?? 'none';
  const classChoice = progress.data?.class_choice ?? null;
  const effectiveClass = classChoice && classChoice !== 'none' ? classChoice : classAuto;

  const setClass = useMutation({
    mutationFn: (cls: string) =>
      api('/api/me/class', { method: 'POST', body: { class: cls } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me-progress'] });
    },
  });

  const iconFor = (cls: string): string =>
    CLASS_OPTIONS.find((o) => o.value === cls)?.icon ?? '·';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
      }}
      className="card space-y-4 max-w-xl mx-auto"
    >
      <div>
        <h2 className="section-flourish text-sm font-semibold uppercase tracking-wide text-ink-muted">Class</h2>
        <p className="text-xs text-ink-dim">
          Your class determines which competencies you earn bonus XP on. The auto-class
          is computed from your highest-progress competencies; pick a different one
          here to override it.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="label">Current class</p>
          <p className="mt-1 text-lg font-semibold">
            {iconFor(effectiveClass)}{' '}
            {effectiveClass === 'none'
              ? 'Not yet earned'
              : effectiveClass.charAt(0).toUpperCase() + effectiveClass.slice(1)}
          </p>
          <p className="text-xs text-ink-dim">
            Auto: {classAuto === 'none' ? '—' : classAuto}
            {classChoice && classChoice !== 'none' ? ' (your choice)' : ''}
          </p>
        </div>
        <select
          className="input max-w-[10rem]"
          value={classChoice ?? 'none'}
          onChange={(e) => setClass.mutate(e.target.value)}
          disabled={setClass.isPending}
        >
          <option value="none">Auto ({classAuto === 'none' ? '—' : classAuto})</option>
          {CLASS_OPTIONS.filter((o) => o.value !== 'none').map((o) => (
            <option key={o.value} value={o.value}>{o.icon} {o.label}</option>
          ))}
        </select>
      </div>

      {setClass.isError ? (
        <p className="text-sm text-accent-danger">Couldn't save your class choice — try again.</p>
      ) : null}
    </form>
  );
}
